import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Menu, Plus, Search, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

interface SearchResult {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface HeaderProps {
  onMenuClick: () => void;
  onAddEvent: () => void;
  currentView: string;
  onViewChange: (view: string) => void;
  isMobile?: boolean;
  sidebarOpen?: boolean;
  onLogout: () => void;
  clinicName?: string;
  onSearchSelect?: (clientId: number) => void;
}

const Header: React.FC<HeaderProps> = ({
  onMenuClick,
  onAddEvent,
  currentView,
  isMobile = false,
  sidebarOpen = false,
  onLogout,
  clinicName,
  onSearchSelect,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const doSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setSearching(true);
    setShowResults(true);
    try {
      const data = await apiService.get(`/api/patients/search/${encodeURIComponent(query.trim())}`);
      if (data.success && data.data) {
        setSearchResults(data.data);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleResultClick = (client: SearchResult) => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    onSearchSelect?.(client.id);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const TITLES: Record<string, string> = {
    calendar: t('navigation.calendar'),
    clients: t('navigation.clients'),
    procedures: t('navigation.procedures'),
    'medical-records': t('navigation.clinical', 'Clínico'),
    config: t('navigation.configuration'),
    logs: t('navigation.systemLogs'),
    admin: t('navigation.management'),
    // screens reachable from the sidebar / procedure hub that used to fall back to "Calendar"
    'consent-templates': t('navigation.consentForms', 'Termos'),
    'intake-forms': t('navigation.intakeForms', 'Anamnese'),
    reputation: t('navigation.reputation', 'Reputação'),
    recall: t('navigation.recall', 'Recall'),
    'bulk-message': t('navigation.bulkMessage', 'Mensagem em massa'),
    packages: t('navigation.packages', 'Pacotes'),
    inventory: t('navigation.inventory', 'Estoque'),
    commissions: t('navigation.commissions', 'Comissões'),
    'invoice-manager': t('navigation.invoices', 'Faturas'),
    'financial-dashboard': t('navigation.financial', 'Financeiro'),
    'business-analytics': t('navigation.analytics', 'Analytics'),
    'booking-config': t('navigation.onlineBooking', 'Agendamento online'),
    billing: t('navigation.billing', 'Plano'),
    'message-templates': t('navigation.messageTemplates', 'Modelos de mensagem'),
  };

  const title = TITLES[currentView] || t('navigation.calendar');

  return (
    <header className="topbar">
      {isMobile && (
        <button
          type="button"
          onClick={onMenuClick}
          className="btn-s ghost icon"
          aria-label={sidebarOpen ? t('common.closeSidebar') : t('common.openSidebar')}
          aria-expanded={sidebarOpen}
        >
          <Menu size={16} />
        </button>
      )}

      <div className="title">
        {title}
        {clinicName && (
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--ink-muted)', marginLeft: 8 }}>
            {clinicName}
          </span>
        )}
      </div>

      <div className="topbar-spacer" />

      <div ref={searchRef} style={{ position: 'relative', display: isMobile ? 'none' : 'block' }}>
        <div className="search-input">
          <Search size={14} />
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => { if (searchResults.length > 0) setShowResults(true); }}
          />
        </div>
        {showResults && (
          <div className="search-dropdown">
            {searching && <div className="search-dropdown-item search-loading">{t('common.loading')}…</div>}
            {!searching && searchResults.length === 0 && searchQuery.trim().length >= 2 && (
              <div className="search-dropdown-item search-empty">{t('common.noResults')}</div>
            )}
            {searchResults.map(client => (
              <button
                key={client.id}
                type="button"
                className="search-dropdown-item"
                onClick={() => handleResultClick(client)}
              >
                <span className="search-result-name">{client.name}</span>
                {client.phone && <span className="search-result-detail">{client.phone}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {currentView === 'calendar' && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className={`btn-s primary${isMobile ? ' icon' : ''}`}
            onClick={onAddEvent}
            title={t('calendar.createEvent')}
            aria-label={t('calendar.createEvent')}
          >
            <Plus size={14} />
            {!isMobile && <span>{t('calendar.createEvent')}</span>}
          </button>
        </div>
      )}

      <button
        type="button"
        className="btn-s ghost icon"
        onClick={onLogout}
        title={t('auth.logout')}
        aria-label={t('auth.logout')}
        style={{ marginLeft: '0.5rem' }}
      >
        <LogOut size={16} />
      </button>
    </header>
  );
};

export default React.memo(Header);
